import * as Immutable from 'immutable';
import moment from 'moment';

import { FiltersData } from '../../components/Filters/Filters.types';
import { Choice, DropdownOption } from '../../components/Table/tableFilter.types';
import { datesForHighCharts, localeData } from '../../locales/data';
import { AlphaRangeFilters, DatetimeFilters } from '../data/';
import { allColumnsParents } from '../data/initialColumnsTable';
import { AlphaDetailsSummaryColumns } from '../data/AlphaDetailsSummaryColumns';

import { getTable } from './tables';
import {
  checkIfFloat,
  checkIfInteger,
  checkIfIsValid,
  checkInterval,
  checkSettings,
  checkSettingsWithoutCode,
  getOptionsOrderAndWithoutRepeatedElements,
} from './validation';

export const getLanguageCode = () => (navigator.languages !== undefined ? navigator.languages[0] : navigator.language);

export const intlFormatMessage = (messageID: string, intl: any, values?: any) =>
  intl.formatMessage(
    {
      id: messageID,
      defaultMessage: localeData.en[messageID],
    },
    values
  );

export const checkIfValueExists = (value: any) => value !== undefined && value !== null;

export const intlCurrency = (currency: string | null, value: number, currencyDisplay: string = 'symbol') => {
  if (currency && (value || value === 0)) {
    return new Intl.NumberFormat(getLanguageCode(), { style: 'currency', currency, currencyDisplay }).format(value);
  } else {
    return '';
  }
};

const isString = (value: any): value is string => {
  return typeof value === 'string';
};

const addModifiers = (baseClass: string, modifiers?: any[]) => {
  const modifiersArray = modifiers ?? [];
  const mdfrs = modifiersArray
    .filter(isString)
    .filter(val => val !== '')
    .map((modifier: string) => baseClass.concat('--', modifier));
  if (mdfrs.length === 0) {
    return baseClass;
  }
  return baseClass.concat(' ', mdfrs.join(' '));
};

export const uniqueArray = (data: any[]) => data.filter((elem, pos, arr) => arr.indexOf(elem) === pos);

export const removeDuplicateObj = (data: any, prop: any) =>
  data.filter((obj: any, pos: any, arr: any) => arr.map((mapObj: any) => mapObj[prop]).indexOf(obj[prop]) === pos);

export const MONACO_EDITOR_DEFAULT_LINE_HEIGHT = 20;

export const calculateEditorHeight = (
  numberOfLines: number,
  options?: {
    lineHeight?: number;
    maxVisibleLines?: number;
  }
) => {
  const lineHeight = options?.lineHeight ?? MONACO_EDITOR_DEFAULT_LINE_HEIGHT;
  if (options?.maxVisibleLines) numberOfLines = Math.min(numberOfLines, options.maxVisibleLines);
  return numberOfLines * lineHeight;
};

export const validCustomFilters = (filters: FiltersData[]) => {
  const rangeFilters: any = AlphaRangeFilters;
  let validFilter: any[] = [];

  if (Array.isArray(filters)) {
    filters.forEach((filter: FiltersData) => {
      if (
        (filter.typeValue !== '' && filter.operatorValue !== '' && rangeFilters.includes(filter.typeValue) === false) ||
        (rangeFilters.includes(filter.typeValue) && filter.operatorValue !== '' && filter.filterValue !== '')
      ) {
        validFilter = validFilter.concat(filter);
      }
    });
  }

  return validFilter;
};

export const setCustomFilters = (newFilters: FiltersData[]) => {
  let data: Object[] = [];
  let typeDataFilter: any = [];
  const rangeFilters: any = AlphaRangeFilters;

  newFilters.forEach((filter: FiltersData) => {
    if (filter.filterCategory === 'is' || rangeFilters.includes(filter.typeValue)) {
      if (typeDataFilter.includes(filter.typeValue)) {
        data = data.map((elem: any) => {
          if (elem.id === filter.typeValue) {
            return Object.assign({}, elem, { value: `${elem.value}&${filter.operatorValue}${filter.filterValue}` });
          } else {
            return elem;
          }
        });
      } else {
        typeDataFilter = typeDataFilter.concat(filter.typeValue);
        data = data.concat({ id: filter.typeValue, value: `${filter.operatorValue}${filter.filterValue}` });
      }
    } else {
      data = data.concat({ id: filter.typeValue, value: filter.operatorValue });
    }
  });

  return data;
};

export const setRangeAlphaFiltersToCustomFilters = (value: string, typeValue: string, filterCategory: string) => {
  let operatorValue: any = value.match(/^>=|=|!=|<=|<|>/);
  operatorValue = operatorValue && operatorValue[0];

  const filterValue = value.replace(/^(>=|=|!=|<=|<|>)/, '');
  if (operatorValue) {
    const changeRangeOperadores: any = { '>=': '≥', '<=': '≤', '!=': '≠' };
    operatorValue = />=|<=|!=/.exec(operatorValue)
      ? operatorValue.replace(/>=|<=|!=/, (match: string) => changeRangeOperadores[match])
      : operatorValue;

    return { typeValue, operatorValue, filterValue, filterCategory: filterCategory };
  } else {
    return false;
  }
};

export const convertAlphaFiltersToCustomFilters = (filters: any) => {
  let data: Object[] = [];
  const rangeFilters: any = AlphaRangeFilters;
  let category: any;
  if (filters) {
    const regexFilter = new RegExp(`${allColumnsParents.join('\\.|')}\\.`);
    Object.keys(filters).forEach((key: string) => {
      const keyWithOutParent = key.replace(regexFilter, '');
      const dateTimeField = keyWithOutParent.replace(/<|>/, '');

      if (/^[\w]+<$/.test(keyWithOutParent) === true && DatetimeFilters.includes(dateTimeField)) {
        return;
      }
      const newCategory = key.match(regexFilter);

      if (newCategory) {
        category = newCategory[0].replace('.', '');
      }
      const regex = new RegExp(`${allColumnsParents.join('\\.|')}\\.|>`);
      const name = key.replace(regex, '');
      if (rangeFilters.includes(name)) {
        if (/&+/.exec(filters[key])) {
          filters[key] = filters[key].trim();
          filters[key].split('&').forEach((val: string) => {
            const trimValue = val.trim();
            const value = /^(<|>|=|!)/.exec(trimValue) === null ? `=${trimValue}` : trimValue;
            const newData = setRangeAlphaFiltersToCustomFilters(value, name, category);
            if (newData !== false) {
              data = data.concat(newData);
            }
          });
        } else {
          filters[key] = filters[key].trim();
          const value = /^(<|>|=|!)/.exec(filters[key]) === null ? `=${filters[key]}` : filters[key];
          const newData = setRangeAlphaFiltersToCustomFilters(value, name, category);
          if (newData !== false) {
            data = data.concat(newData);
          }
        }
      } else if (/^[\w]+>$/.test(keyWithOutParent) === true && DatetimeFilters.includes(dateTimeField)) {
        const value = [moment(filters[key]).toISOString(), moment(filters[`${dateTimeField}<`]).toISOString()];
        data = data.concat({ typeValue: dateTimeField, operatorValue: value, filterValue: '' });
      } else {
        const value = filters[key].replace(/(~|=)/, '');
        data = data.concat({
          typeValue: name,
          operatorValue: isNaN(value) ? value : Number(value),
          filterValue: '',
          filterCategory: category,
        });
      }
    });
  }

  return data;
};

export const concatStringInArrayOfOBjects = (data: any) => {
  let newArray: any = [];
  Object.keys(data).map((element: string) => {
    if (typeof data[element] === 'string') {
      newArray = [{ msg: data[element], defaultMessages: data[element] }].concat(newArray);
    } else if (typeof data[element] === 'object') {
      newArray = newArray.concat(concatStringInArrayOfOBjects(data[element]));
    }
  });

  return newArray;
};

const convertArrayFromDropdown = (choices: any) =>
  choices.map((choice: any, index: number) => ({
    key: `${choice.label}-${choice.value.toString()}-${index}`,
    text: choice.label,
    value: choice.value.toString(),
  }));

export const convertArrayFromDropdownStrings = (choices: any) =>
  choices.map((value: any, index: number) => ({
    key: `${value}-${index}`,
    text: value,
    value: value,
  }));

const convertArrayFromDropdownProperties = (choices: Choice[]): DropdownOption[] =>
  choices.map((choice: Choice, index: number) => ({
    key: `${choice.label}-${choice.value}-${index}`,
    text: choice.label,
    value: choice.value,
  }));

const allOptions = [
  'performance-section',
  'pnl',
  'sharpe',
  'turnover',
  'coverage-section',
  'actual-weight',
  'coverage',
  'coverage-by-capitalization',
  'coverage-by-country',
  'coverage-by-sector',
  'coverage-by-geography',
  'coverage-by-industry',
  'average-size-section',
  'average-size-by-capitalization',
  'average-size-by-country',
  'average-size-by-sector',
  'average-size-by-industry',
  'average-size-by-geography',
  'pnl-section',
  'pnl-by-capitalization',
  'pnl-by-country',
  'pnl-by-sector',
  'pnl-by-industry',
  'pnl-by-geography',
  'sharpe-section',
  'sharpe-by-capitalization',
  'sharpe-by-country',
  'sharpe-by-sector',
  'sharpe-by-industry',
  'sharpe-by-geography',
  'average-value-section',
  'average-value-by-capitalization',
  'average-value-by-country',
  'average-value-by-sector',
  'average-value-by-geography',
  'average-value-by-industry',
];

const sections: any = {
  'performance-section': 'Performance',
  'coverage-section': 'Coverage',
  'average-size-section': 'Average Size',
  'pnl-section': 'PnL',
  'sharpe-section': 'Sharpe',
  'average-value-section': 'Abs Average Value',
};

const convertArrayFromDropdownRecordSet = (choices: any[], hasVizualization: boolean) => {
  let categories = [
    'performance-section',
    'coverage-section',
    'average-size-section',
    'pnl-section',
    'sharpe-section',
    'average-value-section',
  ];
  if (!hasVizualization) {
    categories = ['performance-section'];
  }
  let optionsUsed: any = [];
  if (choices.length > 1) {
    return allOptions
      .map((choice: string, index: number) => {
        const option = choices.find((el: any) => el.name === choice);
        if (categories.indexOf(choice) > -1 && optionsUsed.indexOf(choice) === -1) {
          optionsUsed = optionsUsed.concat([choice]);

          return {
            key: `${choice}-${index}`,
            text: sections[choice],
            value: choice,
            disabled: true,
          };
        } else if (option && optionsUsed.indexOf(choice) === -1) {
          optionsUsed = optionsUsed.concat([choice]);

          return {
            key: option.name,
            text: option.title,
            value: option.name,
          };
        } else {
          return null;
        }
      })
      .filter(el => el !== null);
  } else if (choices.length === 1) {
    return {
      key: choices[0].name,
      text: choices[0].title,
      value: choices[0].name,
    };
  } else {
    return null;
  }
};

const getChoicesByRegion = (choices: any, region: any) => convertArrayFromDropdown(choices[region]);

export const changeBigNumberFormat = (n: number, d: number, type = null) => {
  if (n === null) {
    return n;
  }
  const yValue = n;
  const numericSymbols = ['', 'K', 'M', 'B', 'T'];
  const breaks = [5, 8, 11, 14, 17];
  const numberSize = Math.ceil(Math.log(Math.abs(yValue) + 1) / Math.LN10);
  const index = breaks.findIndex((element: number) => numberSize < element);
  let ret;
  let twoDecimalCaseOption = { minimumFractionDigits: d, maximumFractionDigits: d };
  if (type === 'bookSize') {
    twoDecimalCaseOption = { minimumFractionDigits: 0, maximumFractionDigits: 0 };
  }
  if (index === 0) {
    ret = `${yValue.toLocaleString(getLanguageCode(), twoDecimalCaseOption)}`;
  } else {
    ret = `${(yValue / Math.pow(10, breaks[index - 1] - 2)).toLocaleString(getLanguageCode(), twoDecimalCaseOption)}${
      numericSymbols[index]
    }`;
  }

  return ret;
};

interface ChangeSmallNumberFormatProps {
  value: number;
  type?: string;
  toFixedValue?: number;
  isCheck?: boolean;
  hasPercentageSymbol?: boolean;
}

export const changeSmallNumberFormat = ({
  value,
  type,
  toFixedValue = 2,
  isCheck = false,
  hasPercentageSymbol = true,
}: ChangeSmallNumberFormatProps) => {
  let result = '';
  let twoDecimalCaseOption = { minimumFractionDigits: toFixedValue, maximumFractionDigits: toFixedValue };
  const languageCode = getLanguageCode();
  if (type === 'percent') {
    value = value * 100;
    if (value % 1 === 0 && isCheck) {
      twoDecimalCaseOption = { minimumFractionDigits: 0, maximumFractionDigits: 0 };
      result = value.toLocaleString(languageCode, twoDecimalCaseOption);
    } else {
      result = value.toLocaleString(languageCode, twoDecimalCaseOption);
    }

    return hasPercentageSymbol ? `${result}%` : result;
  } else if (type === 'permyriad') {
    value = value * 10000;
    if (value % 1 === 0 && isCheck) {
      twoDecimalCaseOption = { minimumFractionDigits: 0, maximumFractionDigits: 0 };
      result = value.toLocaleString(languageCode, twoDecimalCaseOption);
    } else {
      result = value.toLocaleString(languageCode, twoDecimalCaseOption);
    }

    return hasPercentageSymbol ? `${result}‱` : result;
  } else if (type === 'permillion') {
    value = value * 1000000;
    if (value % 1 === 0 && isCheck) {
      twoDecimalCaseOption = { minimumFractionDigits: 0, maximumFractionDigits: 0 };
      result = value.toLocaleString(languageCode, twoDecimalCaseOption);
    } else {
      result = value.toLocaleString(languageCode, twoDecimalCaseOption);
    }

    return `${result}`;
  } else {
    if (value % 1 === 0 && isCheck) {
      twoDecimalCaseOption = { minimumFractionDigits: 0, maximumFractionDigits: 0 };

      return value.toLocaleString(languageCode, twoDecimalCaseOption);
    } else {
      return value.toLocaleString(languageCode, twoDecimalCaseOption);
    }
  }
};

export const localizeNumber = (value: number) => {
  if (value > 100 || value < -100) {
    return changeSmallNumberFormat({ value: value });
  } else {
    return changeBigNumberFormat(value, 2);
  }
};

export const localiseValue = (value: number | string | null | undefined, type: string) => {
  if (
    (type === 'decimal' || type === 'percent' || type === 'decimal' || type === 'permyriad' || type === 'amount' || type === 'integer') &&
    typeof value === 'number'
  ) {
    return changeSmallNumberFormat({ value, type });
  } else {
    return value;
  }
};

const changeArrayFormat = (arrayNums: any, tableInfoTypes: any) =>
  arrayNums.map((num: any, index: number) => {
    if (tableInfoTypes[index].type === 'percent' || tableInfoTypes[index].type === 'permyriad') {
      return num !== null && null !== undefined && changeSmallNumberFormat({ value: num, type: tableInfoTypes[index].type });
    } else if (tableInfoTypes[index].type === 'date') {
      return num !== null && null !== undefined ? moment(num).format('L') : '';
    } else if (typeof num === 'string' || tableInfoTypes[index].type === 'integer') {
      return num;
    } else {
      return changeBigNumberFormat(num, 2, tableInfoTypes[index].name);
    }
  });

const changeTableFormat = (table: any, tableInfoTypes: any) => table.map((arrayNum: any) => changeArrayFormat(arrayNum, tableInfoTypes));

const checkCheckingStatus = (data: any) => {
  const filteredData = data.filter((check: any) => check.result === 'FAIL');

  return filteredData.length > 0 ? false : true;
};

export const filterDataGivenTwoHeaders = (data: any, currentSchema: any, newSchema: any) => {
  const arraysOfIndexesToRemove: any = [];
  const updatedSchema: any = [];
  const newSchemaInObject: any = {};
  const oldSchemaInObject: any = {};
  newSchema.map((property: any) => {
    newSchemaInObject[property.id] = true;
  });
  currentSchema.map((property: any, index: number) => {
    if (!newSchemaInObject[property.name]) {
      arraysOfIndexesToRemove.push(index);
      oldSchemaInObject[property.name] = index;
    } else {
      updatedSchema.push(property);
      oldSchemaInObject[property.name] = index;
    }
  });
  const orderedData = data.map((line: any) => newSchema.map((property: any) => line[oldSchemaInObject[property.id]]));

  return { schema: updatedSchema, data: orderedData };
};

export const mapOrder = (array: any[], order: any[], key: string) => {
  array.sort((a, b) => {
    const A = a[key];
    const B = b[key];

    if (order.indexOf(A) > order.indexOf(B)) {
      return 1;
    } else {
      return -1;
    }
  });

  return array;
};

export const getLanguangeDates = (language: string) => {
  if (datesForHighCharts[language]) {
    return datesForHighCharts[language];
  } else {
    return datesForHighCharts.en;
  }
};

export const getMonthByNumber = (index: number) => {
  const months = getLanguangeDates(getLanguageCode()).months;

  return months[index];
};

export const getChartTable = (alphaDetail: any) => {
  const chartTable: any = {
    is: { data: [], header: [], types: [], dataType: [] },
    os: { data: [], header: [], types: [], dataType: [] },
    prod: { data: [], header: [], types: [], dataType: [] },
  };
  Object.keys(chartTable).map((key: string) => {
    if (key === 'is') {
      if (alphaDetail.getIn(['is', 'details'])) {
        const dataIs = alphaDetail.getIn(['is', 'details']).toJS();
        let tableRowsRecords = changeTableFormat(dataIs.records, dataIs.schema.properties);
        const tableHeader = AlphaDetailsSummaryColumns.is.performance;
        const dataUpdated = filterDataGivenTwoHeaders(tableRowsRecords, dataIs.schema.properties, tableHeader);
        tableRowsRecords = dataUpdated.data;
        chartTable.is.data = tableRowsRecords;
        const header: any = tableHeader;
        chartTable.is.header = header;
        const headerTypes: any = tableHeader.map((value: any) => value.type);
        chartTable.is.types = headerTypes;
        chartTable.is.dataType = dataIs.records[0].map((value: any) => typeof value);
      }
    } else if (alphaDetail.get(key)) {
      const orderedTable: any = [];
      const tableHeader = AlphaDetailsSummaryColumns[key].performance;
      const data = alphaDetail.get(key).toJS();
      tableHeader.map((headerRow: any) => {
        if (data[headerRow.id] === undefined) {
          orderedTable.push(null);
        } else {
          orderedTable.push(data[headerRow.id]);
        }
      });
      chartTable[key].data = [changeTableFormat([orderedTable], tableHeader)];
      chartTable[key].header = tableHeader;
      chartTable[key].types = tableHeader.map((value: any) => value.type);
      chartTable[key].dataType = tableHeader.map((value: any) => typeof data[value.id]);
    } else {
      delete chartTable[key];
    }
  });

  return chartTable;
};

export {
  addModifiers,
  convertArrayFromDropdown,
  convertArrayFromDropdownProperties,
  checkIfInteger,
  checkInterval,
  checkIfFloat,
  checkIfIsValid,
  getTable,
  getChoicesByRegion,
  changeArrayFormat,
  changeTableFormat,
  checkSettings,
  checkSettingsWithoutCode,
  convertArrayFromDropdownRecordSet,
  checkCheckingStatus,
};

export { formatDuration, formatCountWithDuration } from './duration';

export const getEditorTabID = (id: string, childIndex: number | null, state: any) => {
  if (childIndex !== null) {
    const parentTab = state.getIn(['editor', 'tabs']).filter((tab: any) => tab.get('id') === id);
    if (parentTab.getIn([0, 'childTabs'])) {
      const childID = parentTab.getIn([0, 'childTabs', childIndex, 'id']);

      return childID ? childID : id;
    }

    return id;
  }

  return id;
};

export const getAlphaChoiceType = (AlphasOptions: any) => {
  if (!AlphasOptions) {
    return;
  }

  const choices = AlphasOptions.filter((choice: any) => choice.get('type') === 'choice');
  const settings = AlphasOptions.getIn(['settings', 'children']).filter((choice: any) => choice.get('type') === 'choice');

  return choices.merge(Immutable.fromJS(settings));
};

export const getAlphaChoicesOptions = (AlphasOptions: any) => {
  if (!AlphasOptions) {
    return;
  }

  let options = Immutable.Map();
  const choices = getAlphaChoiceType(AlphasOptions);

  choices.mapKeys((key: string) => {
    const value = getOptionsOrderAndWithoutRepeatedElements(choices.getIn([key, 'choices']).toJS());
    options = options.merge(Immutable.fromJS({ [key]: value }));
  });

  return options;
};

export const getAlphaChoiceValueLocalized = (choicesOptions: any, id: string, value: any) => {
  const choices: any = choicesOptions ? choicesOptions.get(id) : undefined;
  if (choices) {
    const newValue: any = choices.filter((choice: any) => choice.get('value') === value);
    value = newValue.size === 1 ? newValue.first().get('text') : value;
  }

  return value;
};

export const getDataSplitByCategory = (references: any) => {
  const topModifiers = ['top-purple', 'top-red', 'top-green'];
  const bottomModifiers = ['references-first', 'references-second', 'references-third'];
  let index = 0;
  let organizedReferences = Immutable.Map();
  if (references) {
    references.map((referenceItem: any) => {
      const category = referenceItem.get('category');
      if (organizedReferences.getIn([category])) {
        organizedReferences = organizedReferences.updateIn([category, 'list'], (referencesList: any) => referencesList.push(referenceItem));
      } else {
        organizedReferences = organizedReferences.setIn(
          [category],
          Immutable.fromJS({
            list: [referenceItem],
            modifiers: [topModifiers[index % topModifiers.length]],
            modifiersList: [bottomModifiers[index % bottomModifiers.length]],
            category,
          })
        );
        index = index + 1;
      }
    });

    return organizedReferences.toList();
  }

  return null;
};

export const getStatusModifier = (status: string, competitionFinished?: boolean, isConsultant?: boolean) => {
  if (isConsultant || competitionFinished) {
    return 'gray';
  }

  switch (status) {
    case 'ACCEPTED':
    case 'REQUIRED':
      return 'green';
    case 'PENDING':
    case 'REJECTED':
      return 'blue';
    default:
      return 'gray';
  }
};

export const getRankMessage = (status: string) => {
  switch (status) {
    case 'PENDING':
    case 'REJECTED':
      return 'Enroll';
    case 'REQUIRED':
    case 'ACCEPTED':
      return 'Rank';
    case 'EXCLUDED':
      return 'Restricted';
    default:
      return 'Rank';
  }
};

export const getStatusMessage = (status: string, intl: any, competitionFinished = false) => {
  if (competitionFinished) {
    return intl.formatMessage({ id: 'competition.concluded', defaultMessage: localeData.en['competition.concluded'] });
  } else {
    switch (status) {
      case 'PENDING':
        return intl.formatMessage({ id: 'competition.pending', defaultMessage: localeData.en['competition.pending'] });
      case 'REJECTED':
        return intl.formatMessage({ id: 'competition.rejected', defaultMessage: localeData.en['competition.rejected'] });
      case 'REQUIRED':
        return intl.formatMessage({ id: 'competition.required', defaultMessage: localeData.en['competition.required'] });
      case 'EXCLUDED':
        return intl.formatMessage({ id: 'competition.excluded', defaultMessage: localeData.en['competition.excluded'] });
      default:
        return intl.formatMessage({ id: 'competition.accepted', defaultMessage: localeData.en['competition.accepted'] });
    }
  }
};

export const slugify = (text: string) =>
  text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w\-]+/g, '') // Remove all non-word chars
    .replace(/\-\-+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, ''); // Trim - from end of text

export const capitalizeFirstLetter = (str: string) => (typeof str === 'string' ? str.charAt(0).toUpperCase() + str.slice(1) : str);
export const lowcaseFirstLetter = (str: string) => (typeof str === 'string' ? str.charAt(0).toLowerCase() + str.slice(1) : str);

export const normalizeCapitalization = (str?: string) =>
  typeof str === 'string' ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;

export const startWithWhitespace = (text: string) => /^\s/.test(text);

export const prefixEditTabMsg = 'editor-tab-';
export const booleanFilters = Immutable.List(['favorite', 'hidden']);
// Inverts boolean values of filters e.g. 'hidden': true => false
export const booleanFiltersInvert = Immutable.List();

export const getHeaderHeight = () => {
  const header = document.getElementById('header');
  if (header) {
    return header.offsetHeight;
  }

  return 0;
};

export const cleanTabFlashMessages = ({ id, state, dispatch, removeFlashContexts }: any) => {
  const parentTab = state.getIn(['editor', 'tabs']).find((tab: any) => tab.get('id') === id);
  const childTabs = parentTab && parentTab.get('childTabs');
  let ctxToRemove = [`${prefixEditTabMsg}${id}`];
  if (childTabs) {
    childTabs.map((tab: any) => {
      ctxToRemove = ctxToRemove.concat([`${prefixEditTabMsg}${tab.get('id')}`]);
    });
  }
  dispatch(removeFlashContexts(['editor-code'], ctxToRemove));
  dispatch(removeFlashContexts(['simulation-alphas'], ctxToRemove));
};

export const UTM_PARAMETERS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

export const UTM_COOKIE_NAME = '__wq_utmz';

export function setUTMCookie() {
  if (window.location.hostname !== document.referrer.split('/')[2]) {
    const parameters = [];
    for (const element of window.location.search.substring(1).split('&')) {
      const parameter = element.split('=');
      if (parameter.length === 2) {
        const name = parameter[0];
        const value = parameter[1];
        if (UTM_PARAMETERS.indexOf(name) >= 0) {
          parameters.push(`${name}=${value}`);
        }
      }
    }

    if (parameters.length > 0) {
      const expires = new Date(Date.now() + 15552000000);
      const topLevelDomain = window.location.hostname.replace(/^.+\.([^.]+\.[^.]+)$/, '$1');
      document.cookie = `${UTM_COOKIE_NAME}=${parameters.join('&')};
      expires=${expires.toISOString()}; domain=${topLevelDomain}; path=/; secure`;
    }
  }
}

export function parseUTMCampaign(utm: string) {
  if (!utm) {
    return null;
  }

  const campaign: any = {};
  for (const element of utm.split('&')) {
    const parameter = element.split('=');
    if (parameter.length === 2) {
      const name = parameter[0];
      const value = parameter[1];
      if (UTM_PARAMETERS.indexOf(name) >= 0) {
        campaign[name.substring(4)] = value;
      }
    }
  }

  if (Object.keys(campaign).length === 0) {
    return null;
  }

  return campaign;
}

export const getReferrerCampaign = (
  referrer: string,
  competitionID: any,
  competitionExists: boolean,
  competitionValue: boolean,
  competitionvalid: boolean
) => {
  let campaign = null;
  if (referrer !== '') {
    campaign = {
      source: 'Individual',
      medium: 'referral',
      content: referrer,
    };

    if (competitionExists && competitionID && competitionValue && competitionvalid) {
      campaign = { ...campaign, campaign: competitionID };
    } else {
      campaign = { ...campaign, campaign: 'Brain' };
    }
  } else {
    campaign = parseUTMCampaign(document.location.search.substring(1));
    if (!campaign) {
      campaign = parseUTMCampaign(
        decodeURIComponent(document.cookie.replace(new RegExp(`(?:(?:^|.*;)\\s*${UTM_COOKIE_NAME}\\s*\\=\\s*([^;]*).*$)|^.*$`), '$1'))
      );

      if (!campaign) {
        campaign = {
          source: 'Individual',
          medium: 'referral',
        };
        if (competitionExists && competitionID && competitionValue && competitionvalid) {
          campaign = { ...campaign, campaign: competitionID };
        } else {
          campaign = { ...campaign, campaign: 'Brain' };
        }
      }
    }
  }

  return campaign;
};

export const compareArrays = (array1: string[], array2: string[]) => {
  let result = true;
  array1.forEach((el1: any, index: number) => {
    if (el1 !== array2[index]) {
      result = false;
    }
  });

  return result;
};

export const isNumberType = (type: string, dataTypeIndex?: string, column?: string) => {
  if (
    type === 'decimal' ||
    type === 'percent' ||
    type === 'decimal' ||
    type === 'permyriad' ||
    type === 'amount' ||
    type === 'integer' ||
    dataTypeIndex === 'number' ||
    column === 'Delay'
  ) {
    return 'summary-table-cell-right';
  } else {
    return 'summary-table-cell-left';
  }
};
export const handleDatePickerDateTime = (startDate: moment.Moment, endDate: moment.Moment) => [
  moment.tz(startDate.format('YYYY-MM-DDT00:00:00'), 'America/New_York').toISOString(),
  moment.tz(endDate.format('YYYY-MM-DDT00:00:00'), 'America/New_York').toISOString(),
];

// Same instant as handleDatePickerDateTime (New York local midnight) but serialized with the
// timezone offset (e.g. 2026-06-04T00:00:00-04:00) instead of UTC Z, as the data explorer API expects.
export const handleDataExplorerDateTime = (startDate: moment.Moment, endDate: moment.Moment) => [
  moment.tz(startDate.format('YYYY-MM-DDT00:00:00'), 'America/New_York').format(),
  moment.tz(endDate.format('YYYY-MM-DDT00:00:00'), 'America/New_York').format(),
];

export const removeStringFromHtml = (html: string, regex = /rthfc-\w+/g) => html.replace(regex, '');

export const getQueryString = (name: string, url: string) => {
  name = name.replace(/[\[\]]/g, '\\$&');
  const regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(url);
  if (!results) {
    return null;
  }
  if (!results[2]) {
    return '';
  }

  return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

export const isSimulationEdited = (newCode: any, newSettings: any, alphaDetails: any): boolean => {
  if (!alphaDetails) {
    return true;
  }
  //
  // Is CODE edited?
  let isSimulationNew: boolean = false;
  isSimulationNew = !(alphaDetails.get('regular')
    ? alphaDetails.getIn(['regular', 'code']) === newCode
    : alphaDetails.getIn(['selection', 'code']) === newCode.selection && alphaDetails.getIn(['combo', 'code']) === newCode.combo);

  if (isSimulationNew) {
    return isSimulationNew;
  }
  //
  // Is SETTINGS edited?
  Object.keys(newSettings).forEach((setting: any) => {
    const newSettingValue = newSettings[setting].value;
    const alphaSettingValue = alphaDetails.getIn(['settings', setting]);
    if (alphaSettingValue && newSettingValue && String(newSettingValue) !== String(alphaSettingValue)) {
      isSimulationNew = true;
    }
  });

  return isSimulationNew;
};

export function formatDate(date: moment.Moment) {
  return date.format('MMMM Do, YYYY');
}

export const defaultDateTimeFormat = 'YYYY-MM-DDTHH:mm:ssZ';

export function makeMomentDate(date: string) {
  return moment(date, 'YYYY-M-DDTHH:mm:ssZ', true);
}

export function formatDateTime(date: moment.Moment) {
  return date.tz(moment.tz.guess()).format('MMMM Do, YYYY LTS z');
}

export function formatMonthYear(date?: string) {
  if (!date) return '-';
  // Parse as UTC: date-only strings like "2026-04-01" have no time/timezone component.
  // moment() would parse them as UTC midnight then convert to local time, flipping the month.
  const parsed = moment.utc(date);
  return parsed.isValid() ? parsed.format('MM/YYYY') : '-';
}
